// The tech tree: a genuine DAG. Techs have prerequisites within their
// branch, several are gated behind PHYSICAL buildings you must construct in
// your bailey (and can lose to raids), and the military branch ends in a
// mutually exclusive doctrine choice. Research is per team; effects are
// looked up by the sim through the helper functions below.

import type { BuildingKind, CharacterState, GameState, Team } from './types';

export type TechBranch = 'military' | 'economy' | 'royal';

export interface Tech {
  id: string;
  branch: TechBranch;
  cost: number;
  label: string;
  desc: string;
  /** All of these must be owned first. */
  prereqs: string[];
  /** Requires this building standing and complete. */
  needsBuilding?: BuildingKind;
  /** Owning the other side of an exclusive pair locks this out forever. */
  excludes?: string;
}

export const TECHS: Tech[] = [
  // ── military (the Smithy is the branch gate) ──
  { id: 'weapon-drills', branch: 'military', cost: 150, label: 'Weapon Drills', desc: '+15% damage for all your units', prereqs: [], needsBuilding: 'smithy' },
  { id: 'shields', branch: 'military', cost: 250, label: 'Shield Wall', desc: 'Your units take 12% less damage', prereqs: ['weapon-drills'] },
  { id: 'unlock-knights', branch: 'military', cost: 300, label: 'Knight Order', desc: 'Unlocks knight waves', prereqs: ['weapon-drills'] },
  { id: 'longbows', branch: 'military', cost: 250, label: 'Longbows', desc: 'Archers: +25% damage, +6 range', prereqs: ['weapon-drills'], needsBuilding: 'range' },
  { id: 'veterans', branch: 'military', cost: 450, label: 'Veteran Corps', desc: 'New units arrive with +25% health', prereqs: ['shields'] },
  { id: 'steel-doctrine', branch: 'military', cost: 600, label: 'Steel Doctrine', desc: '+15% damage — locks out Iron Doctrine', prereqs: ['veterans'], excludes: 'iron-doctrine' },
  { id: 'iron-doctrine', branch: 'military', cost: 600, label: 'Iron Doctrine', desc: '12% less damage taken — locks out Steel Doctrine', prereqs: ['veterans'], excludes: 'steel-doctrine' },
  // ── economy ──
  { id: 'taxation', branch: 'economy', cost: 150, label: 'Taxation', desc: '+2 gold/s', prereqs: [] },
  { id: 'crop-rotation', branch: 'economy', cost: 250, label: 'Crop Rotation', desc: 'Farms produce +1 gold/s each', prereqs: ['taxation'] },
  { id: 'trade-routes', branch: 'economy', cost: 300, label: 'Trade Routes', desc: 'Unlocks markets', prereqs: ['taxation'] },
  { id: 'banking', branch: 'economy', cost: 450, label: 'Banking', desc: '+1 gold/s per 200 stored (max +5)', prereqs: ['trade-routes'], needsBuilding: 'treasury' },
  // ── royal (the Chapel is the branch gate) ──
  { id: 'assassin-training', branch: 'royal', cost: 200, label: 'Assassin Training', desc: 'Your agent: +20% damage, cheaper dodges', prereqs: [], needsBuilding: 'chapel' },
  { id: 'bodyguards', branch: 'royal', cost: 300, label: 'Bodyguards', desc: 'Two extra royal guards join your hall', prereqs: ['assassin-training'] },
  { id: 'fortifications', branch: 'royal', cost: 400, label: 'Fortifications', desc: 'Your guards and King take 20% less damage', prereqs: ['bodyguards'] },
  { id: 'physician', branch: 'royal', cost: 500, label: 'Royal Physician', desc: 'Your King slowly regenerates health', prereqs: ['bodyguards'] },
];

export function techById(id: string): Tech | undefined {
  return TECHS.find((t) => t.id === id);
}

export function hasTech(state: GameState, team: Team, id: string): boolean {
  return state.research[team].includes(id);
}

/** A standing, fully constructed building of this kind. */
export function hasCompleteBuilding(state: GameState, team: Team, kind: BuildingKind): boolean {
  return state.buildings.some(
    (b) => b.team === team && b.kind === kind && b.progress >= 1 && b.hp > 0
  );
}

/** Why a tech can't be researched right now (null = it can). */
export function techBlockReason(state: GameState, team: Team, id: string): string | null {
  const tech = techById(id);
  if (!tech || hasTech(state, team, id)) return 'owned';
  if (tech.excludes && hasTech(state, team, tech.excludes)) {
    return `locked out by ${techById(tech.excludes)?.label ?? tech.excludes}`;
  }
  for (const p of tech.prereqs) {
    if (!hasTech(state, team, p)) return `requires ${techById(p)?.label ?? p}`;
  }
  if (tech.needsBuilding && !hasCompleteBuilding(state, team, tech.needsBuilding)) {
    return `requires a ${BUILDING_LABELS[tech.needsBuilding]}`;
  }
  return null;
}

export const BUILDING_LABELS: Record<BuildingKind, string> = {
  farm: 'Farm',
  market: 'Market',
  smithy: 'Smithy',
  range: 'Archery Range',
  chapel: 'Chapel',
  treasury: 'Treasury',
  housing: 'Housing',
};

export function isTechAvailable(state: GameState, team: Team, id: string): boolean {
  return techBlockReason(state, team, id) === null;
}

// ── effect lookups ───────────────────────────────────────────────────────────

export function offenseMultiplier(state: GameState, c: CharacterState): number {
  let m = 1;
  if (hasTech(state, c.team, 'weapon-drills')) m *= 1.15;
  if (hasTech(state, c.team, 'steel-doctrine')) m *= 1.15;
  if (c.archetype === 'archer' && hasTech(state, c.team, 'longbows')) m *= 1.25;
  if (c.kind === 'agent' && hasTech(state, c.team, 'assassin-training')) m *= 1.2;
  return m;
}

export function defenseMultiplier(state: GameState, c: CharacterState): number {
  let m = 1;
  if (hasTech(state, c.team, 'shields')) m *= 0.88;
  if (hasTech(state, c.team, 'iron-doctrine')) m *= 0.88;
  if (
    (c.brain === 'guard' || c.brain === 'royal' || c.kind === 'king') &&
    hasTech(state, c.team, 'fortifications')
  ) {
    m *= 0.8;
  }
  return m;
}

export function spawnHealthMultiplier(state: GameState, team: Team): number {
  return hasTech(state, team, 'veterans') ? 1.25 : 1;
}

export function dodgeCostMultiplier(state: GameState, c: CharacterState): number {
  return c.kind === 'agent' && hasTech(state, c.team, 'assassin-training') ? 0.7 : 1;
}

/** Longbow tech stretches archer engagement range. */
export function archerRange(state: GameState, team: Team): number {
  return hasTech(state, team, 'longbows') ? 30 : 24;
}

export function waveUnlocked(state: GameState, team: Team, kind: string): boolean {
  if (kind === 'archers') return hasCompleteBuilding(state, team, 'range');
  if (kind === 'knights') return hasTech(state, team, 'unlock-knights');
  if (kind === 'market') return hasTech(state, team, 'trade-routes');
  return true;
}

/** Recompute a team's steady income from base + techs + completed buildings. */
export function recomputeIncome(state: GameState, team: Team): void {
  let income = 3;
  if (hasTech(state, team, 'taxation')) income += 2;
  const farmYield = hasTech(state, team, 'crop-rotation') ? 3 : 2;
  for (const b of state.buildings) {
    if (b.team !== team || b.progress < 1) continue;
    if (b.kind === 'farm') income += farmYield;
    else if (b.kind === 'market') income += 4;
  }
  state.goldIncome[team] = income;
}

/** Banking bonus depends on current savings; applied per tick on top of
 * goldIncome. */
export function bankingBonus(state: GameState, team: Team): number {
  if (!hasTech(state, team, 'banking')) return 0;
  return Math.min(5, Math.floor(state.gold[team] / 200));
}
